import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Button, Card, CardContent, Chip, Divider, LinearProgress, Stack, Typography } from '@mui/joy';

interface MockCampaign {
  id: string;
  name: string;
  date: string;
  time: string;
  location: string;
  assignedDrones: string[];
  readiness: number;
}

// Mock data for upcoming campaigns
const mockCampaigns: MockCampaign[] = [];

interface UpcomingScheduledCampaignsProps {
  campaigns?: typeof mockCampaigns;
}

const UpcomingScheduledCampaigns = ({ campaigns = mockCampaigns }: UpcomingScheduledCampaignsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Function to calculate days remaining
  const getDaysRemaining = (dateString: string) => {
    const today = new Date();
    const campaignDate = new Date(dateString);
    const diffTime = campaignDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Function to get readiness color
  const getReadinessColor = (readiness: number): 'success' | 'warning' | 'danger' => {
    if (readiness >= 90) return 'success';
    if (readiness >= 70) return 'warning';
    return 'danger';
  };

  return (
    <Card variant='outlined'>
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography level='title-md'>{t('dashboard.upcoming_campaigns', 'Upcoming Scheduled Campaigns')}</Typography>
        <Button color='primary' onClick={() => navigate('/campaigns')} size='sm' variant='plain'>
          {t('dashboard.schedule_new', 'Schedule New')}
        </Button>
      </Box>
      <CardContent>
        <Stack divider={<Divider />} spacing={2}>
          {campaigns.map((campaign) => {
            const daysRemaining = getDaysRemaining(campaign.date);
            const readinessColor = getReadinessColor(campaign.readiness);

            return (
              <Box key={campaign.id}>
                <Stack spacing={1}>
                  <Stack alignItems='center' direction='row' justifyContent='space-between'>
                    <Typography level='title-sm'>{campaign.name}</Typography>
                    <Chip
                      color={daysRemaining <= 1 ? 'warning' : 'neutral'}
                      size='sm'
                      startDecorator={<AccessTimeIcon fontSize='small' />}
                      variant='soft'
                    >
                      {(() => {
                        if (daysRemaining === 0) return t('dashboard.today', 'Today');
                        if (daysRemaining === 1) return t('dashboard.tomorrow', 'Tomorrow');
                        return `${daysRemaining} ${t('dashboard.days', 'days')}`;
                      })()}
                    </Chip>
                  </Stack>

                  <Stack direction='row' flexWrap='wrap' spacing={2}>
                    <Stack alignItems='center' direction='row' spacing={0.5}>
                      <CalendarTodayIcon fontSize='small' sx={{ color: 'text.tertiary' }} />
                      <Typography level='body-xs'>
                        {new Date(campaign.date).toLocaleDateString()} {campaign.time}
                      </Typography>
                    </Stack>
                    <Stack alignItems='center' direction='row' spacing={0.5}>
                      <LocationOnIcon fontSize='small' sx={{ color: 'text.tertiary' }} />
                      <Typography level='body-xs'>{campaign.location}</Typography>
                    </Stack>
                  </Stack>

                  <Box>
                    <Stack alignItems='center' direction='row' justifyContent='space-between' mb={0.5}>
                      <Typography level='body-xs'>{t('dashboard.readiness', 'Readiness')}</Typography>
                      <Typography color={readinessColor} level='body-xs'>
                        {campaign.readiness}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      color={readinessColor}
                      determinate
                      sx={{ height: 6, borderRadius: 3 }}
                      value={campaign.readiness}
                    />
                  </Box>

                  <Typography level='body-xs'>
                    <strong>{t('dashboard.assigned_drones', 'Assigned Drones')}:</strong>{' '}
                    {campaign.assignedDrones.join(', ')}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
          {campaigns.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color='neutral' level='body-sm'>
                {t('dashboard.no_campaigns', 'No Upcoming Campaigns')}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UpcomingScheduledCampaigns;
